// ──────────────────────────────────────────────
// ChunkSilenceAnalyzer — deterministic zero-signal detection on finalized chunks
//
// A muted/dead-signal mic produces valid WebM chunks whose decoded samples
// are bit-exact zeros: the container is well-formed, so backend validation
// and Deepgram both pass them through as empty-but-successful. Decoding the
// finalized (independently decodable) chunk on the client is the only
// deterministic way to know "this chunk carries no signal".
//
// See GDN-2204.
// ──────────────────────────────────────────────

/**
 * Peak linear amplitude at or below which a chunk is considered silent.
 * A muted/dead capture decodes to exact zeros; real microphones have a
 * noise floor orders of magnitude above this (-80 dBFS). The headroom
 * only absorbs float rounding introduced by the opus decode.
 */
export const SILENCE_PEAK_FLOOR = 1e-4;

export interface ChunkSilenceResult {
  /** True when the chunk's peak amplitude is at/below SILENCE_PEAK_FLOOR */
  isSilent: boolean;
  /** Peak linear amplitude (0..1) across all channels */
  peakLevel: number;
}

/**
 * Peak absolute amplitude across all channels; NaN if any sample is NaN
 * (corrupt decode must read as "unknown", never as silent). Exported for tests.
 */
export function computePeakLevel(channels: Float32Array[]): number {
  let peak = 0;
  for (const channel of channels) {
    for (let i = 0; i < channel.length; i++) {
      const abs = Math.abs(channel[i]);
      if (Number.isNaN(abs)) return NaN;
      if (abs > peak) peak = abs;
    }
  }
  return peak;
}

/**
 * Decode a finalized chunk and measure its signal level.
 * Returns null when analysis is unavailable (no OfflineAudioContext,
 * decode failure) — callers must treat null as "unknown", never "silent".
 */
export async function analyzeChunkSilence(data: ArrayBuffer): Promise<ChunkSilenceResult | null> {
  if (typeof OfflineAudioContext === 'undefined') return null;

  try {
    // Never rendered — only used for its decoder, so the constructor
    // parameters are irrelevant.
    const ctx = new OfflineAudioContext(1, 1, 48_000);

    // decodeAudioData detaches its input buffer — pass a copy so the
    // chunk data survives for storage/upload.
    const audioBuffer = await ctx.decodeAudioData(data.slice(0));

    const channels: Float32Array[] = [];
    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      channels.push(audioBuffer.getChannelData(i));
    }

    const peakLevel = computePeakLevel(channels);
    if (Number.isNaN(peakLevel)) return null;

    return { isSilent: peakLevel <= SILENCE_PEAK_FLOOR, peakLevel };
  } catch {
    return null;
  }
}
